export const CONSENT_STORAGE_KEY = "ams_cookie_consent_v1";
export const CONSENT_UPDATED_EVENT = "ams:consent-updated";
export const OPEN_CONSENT_EVENT = "ams:open-consent-preferences";

const GOOGLE_ANALYTICS_ID = "G-S48VTGC1JJ";
const GOOGLE_TAG_MANAGER_ID = "GTM-MJPHSMSC";

const DEFAULT_CONSENT = {
  version: 1,
  necessary: true,
  analytics: false,
  marketing: false,
  updatedAt: null,
};

export function hasGlobalPrivacyControl() {
  return (
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  );
}

export function normalizeConsent(consent = {}) {
  return {
    ...DEFAULT_CONSENT,
    ...consent,
    necessary: true,
    analytics: Boolean(consent.analytics),
    marketing: hasGlobalPrivacyControl() ? false : Boolean(consent.marketing),
  };
}

export function getDefaultConsent() {
  return normalizeConsent(DEFAULT_CONSENT);
}

export function hasStoredConsent() {
  if (typeof window === "undefined") return false;

  return window.localStorage.getItem(CONSENT_STORAGE_KEY) !== null;
}

export function getStoredConsent() {
  if (typeof window === "undefined") return getDefaultConsent();

  try {
    const storedConsent = window.localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!storedConsent) return getDefaultConsent();

    return normalizeConsent(JSON.parse(storedConsent));
  } catch (error) {
    return getDefaultConsent();
  }
}

export function saveConsent(consent) {
  if (typeof window === "undefined") return normalizeConsent(consent);

  const normalizedConsent = normalizeConsent({
    ...consent,
    updatedAt: new Date().toISOString(),
  });

  window.localStorage.setItem(
    CONSENT_STORAGE_KEY,
    JSON.stringify(normalizedConsent)
  );
  window.dispatchEvent(
    new CustomEvent(CONSENT_UPDATED_EVENT, { detail: normalizedConsent })
  );

  return normalizedConsent;
}

function appendScript(id, src) {
  if (typeof document === "undefined" || document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function ensureGtag() {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

function updateGoogleConsent(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
  });
}

function loadGoogleAnalytics(path) {
  ensureGtag();

  if (!window.__amsGoogleAnalyticsLoaded) {
    appendScript(
      "ams-google-analytics",
      `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`
    );
    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ANALYTICS_ID, {
      anonymize_ip: true,
      send_page_view: false,
    });
    window.__amsGoogleAnalyticsLoaded = true;
  }

  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

function loadGoogleTagManager() {
  if (window.__amsGoogleTagManagerLoaded) return;

  ensureGtag();
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
  appendScript(
    "ams-google-tag-manager",
    `https://www.googletagmanager.com/gtm.js?id=${GOOGLE_TAG_MANAGER_ID}`
  );
  window.__amsGoogleTagManagerLoaded = true;
}

function loadZohoTracking(path) {
  appendScript("ams-zoho-gclid", "https://forms.zoho.com/js/zf_gclid.js");

  if (path === "/contact" || path.startsWith("/contact/")) {
    appendScript("ams-zoho-crm", "https://crm.zoho.com/crm/javascript/zcga.js");
  }
}

export function loadConsentManagedScripts(consent, path = "/") {
  if (typeof window === "undefined") return;

  const normalizedConsent = normalizeConsent(consent);

  ensureGtag();
  updateGoogleConsent(normalizedConsent);

  if (normalizedConsent.analytics) {
    loadGoogleAnalytics(path);
  }

  if (normalizedConsent.marketing) {
    loadGoogleTagManager();
    loadZohoTracking(path);
  }
}
